import { useCallback, useEffect, useState } from "react";
import {
  clinicalApi,
  type CurrentUser,
  type PatientDetail,
  type WorkflowState,
} from "../../lib/api/clinical";
import {
  LEVEL_LABELS,
  WORKFLOW_LABELS,
  asText,
  daysSince,
  formatDateTime,
  levelClass,
} from "./format";

const NEXT_STATE: Partial<Record<WorkflowState, WorkflowState>> = {
  open: "reviewing",
  reviewing: "intervening",
  intervening: "observing",
  observing: "closed",
};

const INTERVENTIONS = [
  "留置指征复核",
  "导管维护核查",
  "拔管/替代方案评估",
  "24小时反馈病区",
];

interface Props {
  patientId: string;
  user: CurrentUser;
  onClose: () => void;
  onChanged: () => void;
}

export function PatientDrawer({ patientId, user, onClose, onChanged }: Props) {
  const [detail, setDetail] = useState<PatientDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const canWrite = user.role === "admin" || user.role === "infection_control";

  const load = useCallback(async () => {
    const data = await clinicalApi.patientDetail(patientId);
    setDetail(data);
  }, [patientId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load().catch(() => {
      setMessage("加载患者详情失败");
    });
  }, [load]);

  const openWorkflow = detail?.workflows.find(
    (workflow) => !["closed", "cancelled"].includes(workflow.state),
  );
  const latest = detail?.assessments[0];

  async function run(task: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage("");
    try {
      await task();
      await load();
      onChanged();
      setMessage(ok);
    } catch {
      setMessage("操作失败，请重试或联系管理员");
    } finally {
      setBusy(false);
    }
  }

  function advance() {
    if (!openWorkflow) return;
    const next = NEXT_STATE[openWorkflow.state];
    if (!next) return;
    const reason = next === "closed" ? "干预完成，达到闭环条件" : undefined;
    void run(
      () => clinicalApi.transitionWorkflow(openWorkflow.id, next, openWorkflow.version, reason),
      `已推进到「${WORKFLOW_LABELS[next]}」`,
    );
  }

  function notifyWard() {
    if (!openWorkflow || !detail) return;
    const recipient = detail.ward ?? "病区护士站";
    void run(
      () => clinicalApi.notify(openWorkflow.id, recipient, "高危导尿管患者，请复核留置指征并反馈"),
      "已通知病区",
    );
  }

  function logIntervention(name: string) {
    if (!openWorkflow) return;
    void run(
      () => clinicalApi.addWorkflowAction(openWorkflow.id, "intervention", { item: name }),
      `已记录：${name}`,
    );
  }

  function startReview() {
    if (!openWorkflow || !detail) return;
    void run(
      () => clinicalApi.createInfectionReview(openWorkflow.id, detail.id),
      "已转入院感复核",
    );
  }

  return (
    <aside className="drawer" role="dialog" aria-label="患者详情">
      <div className="drawer-head">
        <div>
          <h2>{detail?.external_id ?? "加载中…"}</h2>
          <p>
            {detail?.ward ?? "未分配病区"} · {detail?.bed ?? "床位未知"}
          </p>
        </div>
        <button type="button" onClick={onClose}>
          关闭
        </button>
      </div>

      {message ? (
        <p className="drawer-msg" role="status">
          {message}
        </p>
      ) : null}

      {latest ? (
        <section className={`risk-card ${levelClass(latest.level)}`}>
          <div className="risk-card-top">
            <span className="risk-tier">{LEVEL_LABELS[latest.level]}</span>
            <span className="risk-index">{asText(latest.factors.risk_index)}</span>
          </div>
          <p className="risk-rate">
            同风险层参考感染率 {asText(latest.factors.calibrated_rate_text)}
          </p>
          <p className="risk-action">{asText(latest.factors.action)}</p>
          <ul className="risk-factors">
            {(latest.factors.drivers as string[] | undefined)?.map((factor) => (
              <li key={factor}>{factor}</li>
            ))}
          </ul>
          <p className="risk-note">评估时间 {formatDateTime(latest.assessed_at)}</p>
        </section>
      ) : (
        <p>暂无风险评估记录，可在「风险预测」中生成。</p>
      )}

      {openWorkflow ? (
        <section className="drawer-section">
          <h3>处置闭环 · {WORKFLOW_LABELS[openWorkflow.state]}</h3>
          {canWrite ? (
            <div className="intervention-grid">
              {INTERVENTIONS.map((item) => (
                <button
                  key={item}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    logIntervention(item);
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          ) : null}
          {canWrite ? (
            <div className="drawer-actions">
              <button type="button" disabled={busy} onClick={notifyWard}>
                通知病区
              </button>
              <button type="button" disabled={busy} onClick={startReview}>
                转院感复核
              </button>
              {(() => {
                const next = NEXT_STATE[openWorkflow.state];
                return next ? (
                  <button type="button" className="primary" disabled={busy} onClick={advance}>
                    推进到「{WORKFLOW_LABELS[next]}」
                  </button>
                ) : null;
              })()}
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="drawer-section">
        <h3>导管记录</h3>
        {detail && detail.catheters.length > 0 ? (
          <ul className="plain-list">
            {detail.catheters.map((catheter) => (
              <li key={catheter.id}>
                {catheter.indication} · 留置 {daysSince(catheter.inserted_at) ?? "?"} 天 ·{" "}
                {catheter.active ? "在管" : "已拔除"}
                {canWrite && catheter.active ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void run(
                        () =>
                          clinicalApi.removeCatheter(
                            catheter.id,
                            new Date().toISOString(),
                            "床旁确认拔除",
                          ),
                        "已记录拔管",
                      );
                    }}
                  >
                    记录拔管
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p>暂无导管记录。</p>
        )}
      </section>

      <section className="drawer-section">
        <h3>评估历史</h3>
        <ul className="plain-list">
          {detail?.assessments.slice(0, 8).map((assessment) => (
            <li key={assessment.id}>
              {formatDateTime(assessment.assessed_at)} · {LEVEL_LABELS[assessment.level]} · 指数{" "}
              {asText(assessment.factors.risk_index)}
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}
